<!-- Questions.vue -->
<script setup lang="ts">
import {
  computed,
  inject,
  ref,
  nextTick,
  onUnmounted,
  onMounted,
  provide,
  watch,
} from 'vue';
import { IonSpinner } from '@ionic/vue';
import List from '../QuestionTypes/List.vue';
import Joint from '../QuestionTypes/Joint.vue';
import Cooper from '../QuestionTypes/Cooper.vue';
import Textbox from '../QuestionTypes/Textbox.vue';
import FoodInputHell from '../QuestionTypes/FoodInputHell.vue';
import EnergyInformation from '../QuestionTypes/EnergyInformation.vue';
import EnergyResults from '../QuestionTypes/EnergyResults.vue';
import Alcohol from '../QuestionTypes/Alcohol.vue';
import Pushup from '../QuestionTypes/Pushup.vue';
import TwoValues from '../QuestionTypes/TwoValues.vue';
import Cigarette from '@/components/icons/Cigarette.vue';
import Flower from '@/components/icons/Flower.vue';
import Food from '@/components/icons/Food.vue';
import Handball from '@/components/icons/Handball.vue';
import HandHeart from '@/components/icons/HandHeart.vue';
import People from '@/components/icons/People.vue';
import PersonCheck from '@/components/icons/PersonCheck.vue';
import { useStore } from '@/stores/main';
import { useDebounceFn } from '@vueuse/core';

// Type definition for SectionResult
type SectionResult = {
  pageName: string;
  pageLabel: string;
  count: number;
  results: {
    healthy: number;
    risks: number;
    unknowns: number;
  };
  done: boolean;
  factors: {
    name: string;
    value: number;
  }[];
};

const props = defineProps<{
  sections: any[];
  factors: any;
  data: any;
}>();

const emit = defineEmits<{
  (e: 'saved'): void;
  (e: 'update'): void;
  (e: 'index', value: number): void;
}>();

const colorScheme = inject('colorScheme', 'green');
const nextSectionSlide = inject('nextSlide') as (() => void) | undefined;
const previousSectionSlide = inject('previousSlide') as (() => void) | undefined;
const { shouldResetNavigation, setNavigationReset } = inject(
  'shouldResetNavigation',
  { shouldResetNavigation: ref(false), setNavigationReset: () => {} },
);
const parentHeight = inject('parentHeight', ref(0));

const store = useStore();

const loading = ref(false);
const warningOpen = ref(false);
const questionAnswered = ref(false);
const pendingImproveValue = ref<string | null>(null);

// Optimistic state to show Nöjd/Förbättra section immediately
const hasOptimisticAnswer = ref(false);
// Optimistic state for instant radio button selection
const optimisticSelectedValue = ref<any>(null);
const canSkip = ref(true);
const slides = inject('swiperInstance', ref());
const selectedSlice = ref<number | null>(null);
const questionArea = ref<HTMLElement | null>(null);
const navigationArea = ref<HTMLElement | null>(null);
const scrollButtonVisible = ref(false);
const shouldScroll = ref(false);

// Cleanup variables
let resizeTimeout: number | null = null;
const resizeObserver = new ResizeObserver(() => {
  if (resizeTimeout !== null) {
    clearTimeout(resizeTimeout);
  }
  resizeTimeout = setTimeout(() => {
    if (navigationArea.value) {
      recalculateHeight(0);
    }
  }, 100);
});

// Computed properties
const borderColor = computed(
  () =>
    ({
      purple: 'border-purple-light',
      yellow: 'border-yellow-light',
      green: 'border-green-light',
      blue: 'border-blue-light',
    })[colorScheme] ?? 'border-green-light',
);

const factor = computed(() => {
  const categoryId = props.data?.category_id;
  if (!categoryId || !store.profile.factors) return null;

  const faktor = store.profile.factors[categoryId];
  if (!faktor) return null;

  if (categoryId === 99) {
    faktor.label = faktor.value >= 3 ? 'frisk' : 'risk';
    store.profile.factors[categoryId] = faktor;
  }

  return faktor;
});

const improve = computed(() => {
  // Use pending value for instant feedback, fallback to store value
  if (pendingImproveValue.value !== null) {
    return pendingImproveValue.value;
  }
  const storeImprove = factor.value?.improve;
  return storeImprove !== undefined && storeImprove !== null ? String(storeImprove) : null;
});

const sectionResults = computed(() => {
  if (!props.sections) return [];

  const results: SectionResult[] = [];

  for (const section of props.sections) {
    if (section.pageName !== 'physical') continue;

    if (section.pageName === 'kasam') {
      if (!props.factors) continue;

      const factor = props.factors[24];
      if (!factor) continue;

      const value =
        factor.risk_value !== undefined
          ? factor.risk_value
          : factor.value !== undefined
            ? parseFloat(factor.value)
            : 0;

      results.push({
        pageName: section.pageName,
        pageLabel: section.pageLabel,
        count: 1,
        results: {
          healthy: value >= 3 ? 1 : 0,
          risks: value < 3 ? 1 : 0,
          unknowns: factor ? 0 : 1,
        },
        done: value > 0,
        factors: [{ name: 'KASAM', value}],
      });
      continue;
    }

    if (!section.groups || !Array.isArray(section.groups)) continue;

    const groupsWithFactors = section.groups.filter(
      (group: any) =>
        group.category_id !== null &&
        (group.can_improve || group.category_id === 24) &&
        group.label !== 'physical-strength-results',
    );

    if (groupsWithFactors.length === 0) continue;

    const answeredCategories = groupsWithFactors.map(
      (group: any) => group.category_id,
    );

    const factors = groupsWithFactors.map((g: any) => {
      if (!props.factors) return { name: g.label, value: undefined };

      const factor = props.factors[g.category_id];
      let value: number | undefined = undefined;
      if (factor) {
        value =
          factor.risk_value !== undefined
            ? +factor.risk_value
            : factor.status !== 'unknown' && factor.value !== undefined
              ? +factor.value
              : undefined;
      }

      return { name: g.label || 'Unknown', value };
    });

    let risks = 0,
      healthy = 0,
      unknowns = 0;

    for (const category of answeredCategories) {
      if (!props.factors) {
        ++unknowns;
        continue;
      }

      const factor = props.factors[category];
      if (factor) {
        const status = factor.status || '';
        if (status === 'profile.healthy' || status === 'healthy') {
          ++healthy;
        } else if (status === 'profile.risk' || status === 'risk') {
          ++risks;
        } else {
          ++unknowns;
        }
      } else {
        ++unknowns;
      }
    }

    results.push({
      pageName: section.pageName,
      pageLabel: section.pageLabel || 'Unknown',
      count: healthy + risks + unknowns,
      results: { healthy, risks, unknowns },
      done: unknowns === 0,
      factors,
    });
  }

  return results;
});

const fillColors = [
  'fill-white',
  'fill-red-400',
  'fill-orange-400',
  'fill-amber-300',
  'fill-lime-300',
  'fill-green-400',
];
const bgColors = [
  'bg-white',
  'bg-red-400',
  'bg-orange-400',
  'bg-amber-300',
  'bg-lime-300',
  'bg-green-400',
];
const iconColors = [
  'text-black/50',
  'text-black/60',
  'text-black/60',
  'text-black/50',
  'text-black/60',
  'text-black/60',
];

function circleSegmentFill(section: any) {
  if (section.results.unknowns) return fillColors[0];

  const sum = section.factors
    .map((f: any) => f.value)
    .reduce((a: number, c: number) => a + c, 0);
  const value = Math.round(sum / section.factors.length);
  return fillColors[value];
}

const totalAverageScore = computed(() => {
  if (
    sectionResults.value.length < 1 ||
    !sectionResults.value.every(s => s.results.unknowns <= 0)
  )
    return 0;

  return (
    sectionResults.value
      .map(
        section =>
          section.factors
            .map((f: any) => f.value)
            .reduce((a: number, c: number) => a + c, 0) /
          section.factors.length,
      )
      .reduce((a: number, c: number) => a + c, 0) / sectionResults.value.length
  );
});

function middleCircleFill() {
  return fillColors[Math.round(totalAverageScore.value)];
}

const iconDist = 23;
const iconHilightDist = 25;

function highlightIcon(index: number, total: number) {
  if (index === selectedSlice.value) {
    const a = ((index + 0.5) / total) * Math.PI * 2;
    return `translate3d(-50%, -50%, 0) translate3d(${Math.sin(a) * iconHilightDist}px, ${-Math.cos(a) * iconHilightDist}px, 0) scale(1.25)`;
  }
  return `translate3d(-50%, -50%, 0)`;
}

function icon(index: number) {
  return [Handball, PersonCheck, HandHeart, Cigarette, Food, Flower, People][
    index
  ];
}

function iconX(index: number, total: number) {
  const a = ((index + 0.5) / total) * Math.PI * 2;
  return 50 + Math.sin(a) * iconDist + '%';
}

function iconY(index: number, total: number) {
  const a = ((index + 0.5) / total) * Math.PI * 2;
  return 50 - Math.cos(a) * iconDist + '%';
}

function iconColor(section: any) {
  if (section.results.unknowns) return iconColors[0];
  return iconColors[
    1 + Math.floor((section.results.healthy / section.count) * 4)
  ];
}

function highlightSwatch(index: number) {
  if (selectedSlice.value === null) return null;

  const section = sectionResults.value[selectedSlice.value];
  const sum = section.factors
    .map((f: any) => f.value)
    .reduce((a: number, c: number) => a + c, 0);
  const value = Math.round(sum / section.factors.length);

  return value === index ? 'scale-150 border border-solid border-white' : null;
}

const chartColors = computed(() =>
  sectionResults.value.map(section => circleSegmentFill(section)),
);

const backgroundColor = computed(
  () =>
    ({
      purple: 'bg-purple-background',
      yellow: 'bg-yellow-background',
      green: 'bg-green-background',
      blue: 'bg-blue-background',
    })[colorScheme],
);

watch(shouldResetNavigation, async newValue => {
  if (!newValue || !slides.value) return;

  slides.value.activeIndex = 0;
  await nextTick();
  setNavigationReset();
});

function nextSlide() {
  const group = props.data.groups[slides.value.activeIndex];
  canSkip.value = true;

  if (group.type === 'food-snack') {
    const count = store.profile.values['energyIntakeNewSnackCount'] ?? 0;
    for (let i = 1; i <= count; i++) {
      if (!store.profile.values[`energyIntakeNewSnack${i}`]) {
        questionAnswered.value = false;
        warningOpen.value = true;
        canSkip.value = false;
        break;
      }
    }
  }

  if (group.questions?.length > 0) {
    if (!store.profile.factors[group.category_id]) {
      if (group.questions[0].type === 'twovalues') {
        group.questions[0].name.split(',').forEach((part: string) => {
          if (store.getProfileValue(part) === null) warningOpen.value = true;
        });
      } else if (group.improve_name === 'fitMethod') {
        if (!store.profile.factors[4]?.value) {
          questionAnswered.value = false;
          warningOpen.value = true;
        } else if (store.profile.factors[4]?.improve === undefined) {
          questionAnswered.value = true;
          warningOpen.value = true;
        }
      } else {
        if (store.getProfileValue(group.questions[0].name) === null)
          warningOpen.value = true;
      }
    } else {
      const factor = store.profile.factors[group.category_id];
      if (group.questions?.length > 1) {
        const numQuestionsAnswered =
          group.questions
            ?.map((question: any) => store.getProfileValue(question.name) !== null)
            .reduce((acc: number, v: boolean) => (v ? acc + 1 : acc), 0) ?? 0;
        if (numQuestionsAnswered !== group.questions?.length) {
          questionAnswered.value = false;
          warningOpen.value = true;
        } else {
          questionAnswered.value = true;
        }
      } else {
        if (factor.status === 'profile.unknown') {
          questionAnswered.value = false;
          warningOpen.value = true;
        } else {
          questionAnswered.value = true;
        }
      }

      if (
        group.can_improve &&
        (factor.improve == null || factor.improve === '0')
      ) {
        warningOpen.value = true;
      }
    }
  }

  if (warningOpen.value) return;

  if (slides.value?.isEnd) {
    nextSectionSlide?.();
    return;
  }

  slides.value.slideNext();
  questionAnswered.value = false;
}

function previousSlide() {
  if (warningOpen.value) return;

  if (slides.value?.isBeginning) {
    previousSectionSlide?.();
    return;
  }

  slides.value.slidePrev();
  questionAnswered.value = false;
}

function slideTo(index: number) {
  if (warningOpen.value) return;

  slides.value.slideTo(index);
  questionAnswered.value = false;
}

function ignoreWarning() {
  warningOpen.value = false;

  if (slides.value?.isEnd) {
    nextSectionSlide?.();
    return;
  }

  slides.value.slideNext();
}

function slideChange(swiper: any) {
  if (!swiper) return;

  store.savePoints ??= {};
  store.savePoints.life ??= {};
  const index = swiper.activeIndex;
  store.savePoints.life.question = index;

  emit('index', index);

  // Reset scroll to top immediately on slide change
  if (questionArea.value) {
    questionArea.value.scrollTop = 0;
  }

  const transitionTime = swiper.params.speed || 300;
  recalculateHeight(transitionTime * 0.5);
  setTimeout(() => {
    recalculateHeight(0);
    // Ensure scroll stays at top after recalculation
    if (questionArea.value) {
      questionArea.value.scrollTop = 0;
    }
  }, transitionTime + 50);
}

function handleSaved() {
  // No-op, can be removed if not needed
}

function handleUpdate() {
  recalculateHeight(0);
}

function handleResizeRequest() {
  handleUpdate();
}

function skipSection() {
  nextSectionSlide?.();
}

function canSkipSection() {
  const index = slides.value?.activeIndex ?? 0;
  return (
    (props.data.pageName === 'kasam' || props.data.pageName === 'physical') &&
    index === 0
  );
}

async function recalculateHeight(timeout = 0) {
  if (!slides.value || !slides.value.wrapperEl) return;

  const scrollTop = questionArea.value?.scrollTop ?? 0;
  const activeIndex = slides.value.activeIndex;
  const activeSlide = slides.value.slides[activeIndex];

  if (!activeSlide) return;

  slides.value.wrapperEl.style.height = 'auto';

  await nextTick();

  const height = activeSlide.scrollHeight || 0;
  const navHeight = navigationArea.value?.clientHeight ?? 0;
  const scrollAreaHeight = parentHeight.value - navHeight;
  const minHeight = 200;
  const finalHeight = Math.max(height, minHeight);

  const restoreScroll = () => {
    if (questionArea.value) questionArea.value.scrollTop = scrollTop;
  };

  if (resizeTimeout !== null) {
    clearTimeout(resizeTimeout);
  }
  resizeTimeout = setTimeout(() => {
    slides.value.wrapperEl.style.height = `${finalHeight}px`;
    restoreScroll();
    scrollButtonVisible.value = finalHeight > scrollAreaHeight;
  }, timeout);
}

watch(questionArea, el => {
  if (el) {
    resizeObserver.observe(el);
  } else if (questionArea.value) {
    resizeObserver.unobserve(questionArea.value);
  }
});

provide('shouldScroll', {
  shouldScroll,
  scrollDown() {
    if (questionArea.value) {
      const scrollHeight = questionArea.value.scrollHeight;
      const scrollTop = questionArea.value.scrollTop;
      const clientHeight = questionArea.value.clientHeight;

      if (scrollTop + clientHeight > scrollHeight * 0.8) {
        shouldScroll.value = true;
      }
    }
  },
  resetShouldScroll() {
    shouldScroll.value = false;
  },
});
watch(shouldScroll, value => {
  if (!value || !questionArea.value) return;

  const currentQuestion = document.querySelector('.question-active');
  if (currentQuestion) {
    currentQuestion.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  } else {
    const currentScroll = questionArea.value.scrollTop;
    const scrollHeight = questionArea.value.scrollHeight;
    const clientHeight = questionArea.value.clientHeight;
    const maxScroll = scrollHeight - clientHeight;
    
    if (currentScroll < maxScroll) {
      // Scroll by 20% of the visible area height
      const scrollIncrement = clientHeight * 0.2;
      
      questionArea.value.scrollTo({
        top: Math.min(currentScroll + scrollIncrement, maxScroll),
        behavior: 'smooth',
      });
    }
  }

  shouldScroll.value = false;
});

provide('questionData', props.data);
provide('optimisticSelectedValue', optimisticSelectedValue);

defineExpose({ slideTo });

const handleChangeDebounced = useDebounceFn(async e => _handleChange(e), 300, {
  maxWait: 500,
});

const currentValue = ref<string | null>(null);
const currentQuestion = ref<string | null>(null);

// Watch factor changes to automatically update currentValue with backend risk_value
// This ensures the 1-5 scale always shows the correct value after API response
watch(() => store.profile.factors, (factors) => {
  const categoryId = props.data?.category_id;
  if (!categoryId || !factors?.[categoryId]) return;
  
  const f = factors[categoryId];
  // Update currentValue with backend's risk_value (takes precedence)
  if (f.risk_value !== undefined && f.risk_value !== null) {
    currentValue.value = String(f.risk_value);
  } else if (f.value !== undefined && f.value !== null && f.value !== -1) {
    currentValue.value = String(f.value);
  }
}, { deep: true });

// Request counter to handle race conditions when clicking quickly
let requestCounter = 0;

async function _handleChange(e: { name: string; value: any }) {
  if (!e || (typeof e.value === 'string' && e.value.trim() === '')) return null;

  const scrollTop = questionArea.value?.scrollTop ?? 0;

  try {
    loading.value = true;
    const result = await store.setProfileValue(e.name, e.value);

    if (result !== null) {
      emit('saved');
      await nextTick();
      return result;
    }
    return null;
  } catch (error) {
    console.error('Error saving profile value:', error);
    return null;
  } finally {
    loading.value = false;
    if (questionArea.value) questionArea.value.scrollTop = scrollTop;
  }
}

function determineCurrentValue(
  questionName: string,
  value: any,
): string | null {
  if (value === '') return '-1';
  if (['strLegs2', 'pushups', 'strAb'].includes(questionName)) {
    const numericValue = +value;
    if (numericValue < 10) return '1';
    if (numericValue < 20) return '2';
    if (numericValue < 30) return '3';
    if (numericValue < 40) return '4';
    return '5';
  }
  return value === 'null' ? null : `${value}`;
}

// Event listener cleanup
const eventListeners: {
  element: HTMLElement | Document;
  type: string;
  handler: EventListener;
}[] = [];

onMounted(() => {
  // Initialize currentValue from existing factor if available
  const categoryId = props.data?.category_id;
  if (categoryId && store.profile.factors?.[categoryId]) {
    const existingFactor = store.profile.factors[categoryId];
    if (existingFactor.value !== undefined && existingFactor.value !== null && existingFactor.value !== '' && existingFactor.value != -1) {
      currentValue.value = String(existingFactor.risk_value ?? existingFactor.value);
    }
  }
  
  if (props.data.questions?.length > 0) {
    currentQuestion.value = props.data.questions[0].name;
    // Only set from question if currentValue not already set from factor
    if (currentValue.value === null) {
      currentValue.value = determineCurrentValue(
        currentQuestion.value,
        store.profile.values[currentQuestion.value],
      );
    }
  }

  if (questionArea.value) {
    const handleWheel = () => {
      shouldScroll.value = false;
      if (resizeTimeout !== null) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {}, 2000);
    };
    const handleTouchMove = () => {
      shouldScroll.value = false;
      if (resizeTimeout !== null) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {}, 2000);
    };
    const handleKeydown = (e: KeyboardEvent) => {
      if (
        ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(
          e.key,
        )
      ) {
        shouldScroll.value = false;
      }
    };

    questionArea.value.addEventListener('wheel', handleWheel);
    questionArea.value.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('keydown', handleKeydown);

    eventListeners.push(
      { element: questionArea.value, type: 'wheel', handler: handleWheel },
      {
        element: questionArea.value,
        type: 'touchmove',
        handler: handleTouchMove,
      },
      { element: document, type: 'keydown', handler: handleKeydown },
    );
  }

  nextTick(() => {
    recalculateHeight(10);
    resizeTimeout = setTimeout(() => recalculateHeight(0), 300);
  });
});

onUnmounted(() => {
  if (questionArea.value) {
    resizeObserver.unobserve(questionArea.value);
  }
  resizeObserver.disconnect();

  eventListeners.forEach(({ element, type, handler }) => {
    element.removeEventListener(type, handler);
  });
  eventListeners.length = 0;

  if (resizeTimeout !== null) {
    clearTimeout(resizeTimeout);
    resizeTimeout = null;
  }
});

const agilityQuestions = [
  'shldrXL',
  'shldrXR',
  'pelIliL',
  'pelIliR',
  'pelHamL',
  'pelHamR',
];

// const kasamQuestions = [
//   'kasam1', 'kasam2', 'kasam3', 'kasam4', 'kasam5', 'kasam6', 'kasam7',
//   'kasam8', 'kasam9', 'kasam10', 'kasam11', 'kasam12', 'kasam13',
// ];

function calcAgility(pendingName?: string, pendingValue?: any) {
  let healthy = 0;

  for (const name of agilityQuestions) {
    // Use pending value if this is the question being changed
    const rawValue = (name === pendingName) ? pendingValue : store.profile.values[name];
    
    // Convert to number for proper comparison (values can be strings '0', '1' or numbers)
    const value = rawValue === null || rawValue === undefined || rawValue === '' 
      ? null 
      : Number(rawValue);
    
    // If any question is unanswered, return neutral value (5 = unknown/neutral)
    if (value === null || value === -1) return 5;
    
    // Value of 1 means "Ja/Klarade" (healthy), 0 means "Nej" (not healthy/risk)
    if (value === 1) healthy++;
  }

  // Original logic: if ANY answer is risk (0), return 1 (risk)
  // Only if ALL answers are healthy (1), return 5 (frisk)
  const hasAnyRisk = healthy < agilityQuestions.length;
  return hasAnyRisk ? 1 : 5;
}

const handleChange = async (e: { name: string; value: any }, type: string) => {
  if (!e || !e.name) return;

  const question_name = e.name;
  currentQuestion.value = agilityQuestions.includes(question_name)
    ? 'agility'
    : question_name;

  // Increment request counter to track this request
  const thisRequest = ++requestCounter;

  // OPTIMISTIC UPDATE - Update currentValue immediately for instant feedback
  // Also set hasOptimisticAnswer so Nöjd/Förbättra section shows immediately
  hasOptimisticAnswer.value = true;
  // Set optimistic selection for instant radio button visual feedback
  optimisticSelectedValue.value = e.value;
  
  // Reset Nöjd/Förbättra selection when answer changes
  // User should reselect based on the new value they see
  pendingImproveValue.value = null;
  
  if (agilityQuestions.includes(question_name)) {
    // Agility: Frontend calcAgility matches backend logic - optimistic OK
    const agilityValue = calcAgility(question_name, e.value);
    currentValue.value = agilityValue.toString();
  } else if (type === 'list-item') {
    // List questions (1-5): The answer value IS the risk_value - optimistic OK
    const val = e.value;
    if (val !== undefined && val !== null && val !== '') {
      currentValue.value = String(val);
    }
  }
  // For Joint and Textbox questions: DON'T set optimistic currentValue
  // Backend calculates the correct risk_value (e.g., balance: 7 → risk 1)
  // We update currentValue after API response in the .then() block below

  // Don't block UI with loading state - let API call happen in background
  handleChangeDebounced(e).then(saveResult => {
    // Ignore stale responses - only process if this is still the latest request
    if (thisRequest !== requestCounter) {
      return; // A newer request was made, ignore this stale response
    }

    if (saveResult !== null) {
      // Update currentValue with the backend's calculated risk_value
      const categoryId = props.data?.category_id;
      if (categoryId && store.profile.factors?.[categoryId]) {
        const updatedFactor = store.profile.factors[categoryId];
        if (updatedFactor.risk_value !== undefined && updatedFactor.risk_value !== null) {
          currentValue.value = String(updatedFactor.risk_value);
        }
      }
      emit('update');
      nextTick().then(() => recalculateHeight(0));
    }
  }).catch(error => {
    console.error('Error in handleChange:', error);
  });
};

function handleImprove(group: any, value: string) {
  // Optimistic update - show selection immediately (synchronous)
  // Don't clear pendingImproveValue - keep it to prevent visual reset
  // when question answer API completes before improve API
  pendingImproveValue.value = value;
  
  // API call in background - don't await
  store.setImproveValue(group.category_id, value).then(result => {
    if (result !== null) {
      emit('update');
    }
    // Don't clear pendingImproveValue - it will be overwritten when user clicks again
  });
}

function questionComponent(type: string) {
  switch (type) {
    case 'fitness-cooper':
      return Cooper;
    case 'list-item':
      return List;
    case 'joint':
      return Joint;
    case 'textbox':
      return Textbox;
    case 'energy-intake':
      return FoodInputHell;
    case 'energy-needs':
      return EnergyInformation;
    case 'energy-balance':
      return EnergyResults;
    case 'alcohol':
      return Alcohol;
    case 'pushups':
      return Pushup;
    case 'twovalues':
      return TwoValues;
    default:
      return undefined;
  }
}
</script>

<template>
  <div
    class="flex flex-col w-full px-8 py-6 pb-14 overflow-y-auto overflow-x-hidden relative lg:text-start"
    :data-factor="data?.improve_name ?? 'null'"
  >
    <div class="flex items-center gap-x-4 mt-2 mb-1">
      <h3
        v-if="data.label !== 'Frågor'"
        class="m-0 font-bold text-left"
        v-text="`${data.label}`"
      ></h3>
    </div>

    <template v-for="question in data.questions">
      <component
        :is="questionComponent(question.type)"
        :data="question"
        @change="handleChange($event, question.type)"
        @update="$emit('update')"
      />

      <template v-if="question.subquestions">
        <template v-for="subquestion in question.subquestions">
          <template
            v-if="
              +store.profile.values[question.name] < +subquestion.toggle_value
            "
          >
            <component
              :is="questionComponent(subquestion.type)"
              :data="subquestion"
              @change="handleChange($event, subquestion.type)"
              @update="$emit('update')"
            />
          </template>
        </template>
      </template>
    </template>

    <div
      v-if="
        data.can_improve &&
        !(data.improve_name && (String(data.improve_name).includes('weight') || String(data.improve_name).includes('length'))) &&
        (
          (factor && factor?.value !== '' && factor?.value != '-1' && factor?.status !== 'unknown' && factor?.status !== 'profile.unknown') ||
          (currentValue && currentValue !== '' && currentValue !== '-1')
        )
      "
      class="border-t border-0 border-solid py-4 text-base mt-6"
      :class="borderColor"
      :data-category="data.category_id"
    >
      <div v-if="data.improve_name === 'agility'" class="mb-6">
        Här ser du ditt samlade resultat på rörlighet.
      </div>
      <div v-if="currentValue || (factor && factor?.value)">
        <span>Du har {{ factor?.label ? (factor.label === 'okänd' ? 'okänt' : factor.label) : (Number(currentValue || factor?.value) >= 3 ? 'frisk' : 'risk') }} värde. </span>

          <ul class="flex flex-col m-0 p-0">
            <li class="list-none">
              <b-radio
                :name="`${data.improve_name}-improve`"
                :color="colorScheme"
                value="1"
                label="Detta är jag nöjd med"
                :label-id="`${data.improve_name}-improve-1-label`"
                @change="handleImprove(data, '1')"
                :model-value="improve"
              />
            </li>
            <li class="list-none">
              <b-radio
                :name="`${data.improve_name}-improve`"
                :color="colorScheme"
                value="2"
                label="Detta vill jag förbättra"
                :label-id="`${data.improve_name}-improve-2-label`"
                @change="handleImprove(data, '2')"
                :model-value="improve"
              />
            </li>
          </ul>
        </div>
      <div class="w-full flex">
        <div
          class="flex justify-center space-x-2 items-center text-base mb-24 mt-10 w-[30%] mx-auto"
        >
          <div class="flex-1 flex text-sm items-center">
            <div
              class="flex-1 border-0 border-t border-solid border-black/30"
            ></div>
            <div class="px-1">Risk</div>
          </div>
          <div class="flex flex-col items-center">
            <div class="flex space-x-2">
            <span
              :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition border',
                  bgColors[1],
                  currentValue === '1' ? 'scale-150' : '',
                ]"
                >1</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[2],
                  currentValue === '2' ? 'scale-150' : '',
                ]"
                >2</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[3],
                  currentValue === '3' ? 'scale-150' : '',
                ]"
                >3</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[4],
                  currentValue === '4' ? 'scale-150' : '',
                ]"
                >4</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[5],
                  currentValue === '5' ? 'scale-150' : '',
                ]"
                >5</span
              >
          </div>
          </div>
          <div class="flex-1 flex text-sm items-center">
            <div class="px-1">Frisk</div>
            <div
              class="flex-1 border-0 border-t border-solid border-black/30"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
